import { useMemo } from "react";
import { ThermoFilters } from "../utils/filtering";
import { TrialType } from "../types";
import { normalizeDateFilterInput } from "../utils/dateTime";

type Props = {
  filters: ThermoFilters;
  onChange: (filters: ThermoFilters) => void;
  onApply: () => void;
  onClear: () => void;
  prodSteps: string[];
  trialOptions: Record<string, string[]>;
  readOnly?: boolean;
  readOnlyValues?: {
    date?: string;
    calibration?: string;
    prodStep?: string;
  };
};

const TRIAL_TYPE_LABELS: Record<string, string> = {
  Glaschemie: "Glass Chemistry",
  Brennstoff: "Fuel",
  "Energieverhältnis": "Energy Ratio",
  Formgebung: "Forming"
};

function formatTrialTypeLabel(trialType: string) {
  return TRIAL_TYPE_LABELS[trialType] || trialType;
}

export default function FilterPanel({
  filters,
  onChange,
  onApply,
  onClear,
  prodSteps,
  trialOptions,
  readOnly = false,
  readOnlyValues
}: Props) {
  const trialTypes = useMemo(
    () =>
      (Object.keys(trialOptions) as TrialType[]).filter(
        (trialType) => !readOnly || (trialOptions[trialType] || []).length > 0
      ),
    [readOnly, trialOptions]
  );

  const handleTrialToggle = (trialType: TrialType, name: string) => {
    if (readOnly) return;
    const current = filters.trials[trialType] || [];
    const next = current.includes(name)
      ? current.filter((v) => v !== name)
      : [...current, name];
    onChange({
      ...filters,
      trials: {
        ...filters.trials,
        [trialType]: next
      }
    });
  };

  return (
    <div className="card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "flex-end"
        }}
      >
        <div>
          <div className="label">Date</div>
          {readOnly ? (
            <div className="input readonly-value">{readOnlyValues?.date || filters.date || "All"}</div>
          ) : (
            <input
              className="input"
              placeholder="YYYY-MM-DD or YYYY-MM-DD HH:MM:SS"
              value={filters.date || ""}
              onChange={(e) => onChange({ ...filters, date: e.target.value || null })}
              onBlur={(e) =>
                onChange({
                  ...filters,
                  date: normalizeDateFilterInput(e.target.value) || null
                })
              }
            />
          )}
        </div>

        <div>
          <div className="label">Calibration</div>
          {readOnly ? (
            <div className="input readonly-value">
              {readOnlyValues?.calibration || filters.calibration || "All"}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <label>
                <input
                  type="radio"
                  name="calibration"
                  value="On Glass"
                  checked={filters.calibration === "On Glass"}
                  onChange={() => onChange({ ...filters, calibration: "On Glass" })}
                />{" "}
                On Glass
              </label>
              <label>
                <input
                  type="radio"
                  name="calibration"
                  value="In Glass"
                  checked={filters.calibration === "In Glass"}
                  onChange={() => onChange({ ...filters, calibration: "In Glass" })}
                />{" "}
                In Glass
              </label>
            </div>
          )}
        </div>

        <div>
          <div className="label">Production Step</div>
          {readOnly ? (
            <div className="input readonly-value">
              {readOnlyValues?.prodStep || filters.prodStep || "All"}
            </div>
          ) : (
            <select
              className="select"
              value={filters.prodStep || ""}
              onChange={(e) =>
                onChange({
                  ...filters,
                  prodStep: e.target.value || null
                })
              }
            >
              <option value="">All Production Steps</option>
              {prodSteps.map((step) => (
                <option key={step} value={step}>
                  {step}
                </option>
              ))}
            </select>
          )}
        </div>

        {!readOnly && (
          <div style={{ display: "flex", gap: 8 }}>
            <button className="button" onClick={onApply}>
              Apply
            </button>
            <button className="button secondary" onClick={onClear}>
              Clear
            </button>
          </div>
        )}
      </div>

      <div className="divider" />

      <div className="grid two">
        {trialTypes.map((trialType) => (
          <div
            key={trialType}
            className="card"
            style={{ background: "var(--surface-elevated)", maxWidth: 520, overflow: "hidden" }}
          >
            <div className="label">{formatTrialTypeLabel(trialType)} Trials</div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                maxHeight: 120,
                overflowY: "auto"
              }}
            >
              {(trialOptions[trialType] || []).map((name) => {
                const checked = !!filters.trials[trialType]?.includes(name);
                return (
                  <label key={name} className="pill">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={readOnly}
                      onChange={() => handleTrialToggle(trialType, name)}
                    />
                    {name}
                  </label>
                );
              })}
              {!readOnly && !trialOptions[trialType]?.length && (
                <span style={{ color: "var(--text-faint)" }}>No trials found</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
